import {
    CSSProperties,
    ForwardedRef,
    forwardRef,
    ForwardRefExoticComponent,
    PropsWithChildren,
    ReactElement,
    ReactNode,
    RefAttributes,
    useEffect,
    useId,
    useImperativeHandle,
    useRef,
    useState,
} from 'react'
import classnames from 'classnames'

import { getStyleProperty } from '@/helpers/style'

import './Popover.scss'

export type PopoverAlign = 'start' | 'end'

export interface PopoverRenderTriggerProps {
    id: string
    open: boolean
}

export interface PopoverHandle {
    close: () => void
    open: () => void
}

export interface PopoverProps extends PropsWithChildren {
    renderTrigger: (props: PopoverRenderTriggerProps) => ReactNode
    label: string
    align?: PopoverAlign
    role?: 'dialog' | 'menu'
    mode?: 'auto' | 'manual'
    className?: string
    contentClassName?: string
}

interface PopoverCssVariables extends CSSProperties {
    '--popover-anchor-name': string
}

const POPOVER_VIEWPORT_MARGIN_PX = 8

// Modern browsers can position (and reposition on overflow, via position-try-fallbacks) the
// popover natively through CSS anchor positioning - see the `@supports (anchor-name: ...)`
// block in Popover.scss. Where that isn't supported yet, this component falls back to
// computing top/left/right in JS below.
const SUPPORTS_ANCHOR_POSITIONING = typeof CSS !== 'undefined'
    && typeof CSS.supports === 'function'
    && CSS.supports('anchor-name', '--popover-support-test')

const PopoverComponent = ({
    renderTrigger,
    label,
    align = 'end',
    role = 'dialog',
    mode = 'auto',
    className = '',
    contentClassName = '',
    children,
}: PopoverProps, ref: ForwardedRef<PopoverHandle>): ReactElement => {
    const popoverId = useId()
    const anchorName = `--popover-${popoverId.replace(/[^a-zA-Z0-9-]/g, '')}`
    const anchorRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)
    const [isOpen, setIsOpen] = useState(false)

    useImperativeHandle(ref, () => ({
        close: () => {
            if (contentRef.current?.matches(':popover-open')) {
                contentRef.current.hidePopover()
            }
        },
        open: () => {
            if (contentRef.current && ! contentRef.current.matches(':popover-open')) {
                contentRef.current.showPopover()
            }
        },
    }), [])

    useEffect(() => {
        const contentElement = contentRef.current

        if (! contentElement) {
            return undefined
        }

        const handleToggle = (event: ToggleEvent): void => setIsOpen(event.newState === 'open')

        contentElement.addEventListener('toggle', handleToggle)

        return () => contentElement.removeEventListener('toggle', handleToggle)
    }, [])

    useEffect(() => {
        if (mode !== 'manual') {
            return undefined
        }

        const anchorElement = anchorRef.current
        const contentElement = contentRef.current

        if (! isOpen || ! anchorElement || ! contentElement) {
            return undefined
        }

        const handlePointerDownOutside = (event: PointerEvent): void => {
            const target = event.target instanceof Node ? event.target : null

            if (
                target
                && ! anchorElement.contains(target)
                && ! contentElement.contains(target)
                && contentElement.matches(':popover-open')
            ) {
                contentElement.hidePopover()
            }
        }

        const timeoutId = setTimeout(() => {
            document.addEventListener('pointerdown', handlePointerDownOutside, true)
        }, 0)

        return () => {
            clearTimeout(timeoutId)
            document.removeEventListener('pointerdown', handlePointerDownOutside, true)
        }
    }, [mode, isOpen])

    useEffect(() => {
        if (SUPPORTS_ANCHOR_POSITIONING) {
            return undefined
        }

        const anchorElement = anchorRef.current
        const contentElement = contentRef.current

        if (! isOpen || ! anchorElement || ! contentElement) {
            return undefined
        }

        const updatePosition = (): void => {
            const anchorRect = anchorElement.getBoundingClientRect()
            const contentWidth = contentElement.offsetWidth
            const desiredLeft = align === 'end' ? anchorRect.right - contentWidth : anchorRect.left
            const maxLeft = window.innerWidth - contentWidth - POPOVER_VIEWPORT_MARGIN_PX
            const offset = parseFloat(getStyleProperty('--popover-offset', anchorElement))

            contentElement.style.top = `${anchorRect.bottom + offset}px`

            // Clamped so the popover never renders off-screen (e.g. a right-aligned popover
            // whose panel is wider than the space to the left of its trigger on mobile).
            contentElement.style.left = `${Math.max(POPOVER_VIEWPORT_MARGIN_PX, Math.min(desiredLeft, maxLeft))}px`
            contentElement.style.right = 'auto'
        }

        updatePosition()

        window.addEventListener('resize', updatePosition)
        window.addEventListener('scroll', updatePosition, true)

        return () => {
            window.removeEventListener('resize', updatePosition)
            window.removeEventListener('scroll', updatePosition, true)
        }
    }, [isOpen, align])

    const cssVariables: PopoverCssVariables = {
        '--popover-anchor-name': anchorName,
    }

    return (
        <div
            className={classnames('paco-popover', `paco-popover--align-${align}`, className)}
            style={cssVariables}
            ref={anchorRef}
        >
            {renderTrigger({ id: popoverId, open: isOpen })}

            <div
                id={popoverId}
                ref={contentRef}
                popover={mode}
                role={role}
                aria-label={label}
                className={`paco-popover__content ${contentClassName}`}
            >
                {children}
            </div>
        </div>
    )
}

export const Popover: ForwardRefExoticComponent<PopoverProps & RefAttributes<PopoverHandle>> = forwardRef(PopoverComponent)
